import { useAuth } from "@clerk/clerk-react";
import { Box } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { GamificationContext } from "contexts/Gamification";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { BrandDealJourney, UpgradeDialogSource } from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { useGamification } from "utils/useGamification";

import OnboardingStepper from "../OnboardingStepper";
import PremiumFeatureUnlocked from "./PremiumFeatureUnlocked";
import CreateNicheTemplateCard from "./components/CreateNicheTemplateCard";
import CreateNicheTemplateCardLoading from "./components/CreateNicheTemplateCardLoading";
import ExperienceFreeLevelOne from "./components/ExperienceFreeLevelOne";
import ExperiencePro from "./components/ExperiencePro";
import ExperienceVa from "./components/ExperienceVa";
import WeeklyConsistencyChallenge from "./components/WeeklyConsistencyChallenge";
import WeeklyConsistencyChallengeLoading from "./components/WeeklyConssitencyChallengeLoading";
import {
  displayFirstTemplateCard,
  displayFirstTemplateCardOnlyForVa,
  isTaskCompleted,
} from "./helpers";
import { OrganizationGamificationTask } from "./schema";
import styles from "./styles";

const Gamification = () => {
  const { profile } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg, gamificationProfile, credits, totalEmailsSent } =
    useContext(OrganizationUserContext);
  const { outreachTemplates, fetchOutreachTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const { pitchCount, setPitchCount } = useContext(GamificationContext);
  const { fetchGamificationProfile, profileLoading } = useGamification();
  const [numOfCardsToRender, setNumOfCardsToRender] = useState<number | null>(
    null,
  );
  const [showPremiumFeatureUnlocked, setShowPremiumFeatureUnlocked] =
    useState(false);
  const showSteps =
    profile?.brandDealJourney === BrandDealJourney.work_regularly &&
    (outreachTemplates.length === 0 || totalEmailsSent === 0);

  const [fetchPitchCountLoading, setFetchPitchCountLoading] =
    useState<boolean>(false);
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);

  const computeNicheCardToRender = () => {
    if (!gamificationProfile) return 0;

    if (
      currentOrg?.isProUser &&
      displayFirstTemplateCard(outreachTemplates, gamificationProfile)
    ) {
      return 1;
    }

    if (currentOrg?.isVaUser) {
      const displayOneCardOnly = displayFirstTemplateCardOnlyForVa(
        gamificationProfile,
        credits,
      );
      if (displayOneCardOnly) {
        return 1;
      }
      return 2;
    }
    return 0;
  };

  const handleFetchProfile = () => {
    // Profile may be pre-populated from /api/organization/auth/user (which
    // returns the FE-formatted profile but not emailStreaks/longestStreak).
    // If streaks haven't been loaded yet, hit the dedicated endpoint to
    // hydrate them — otherwise the weekly-consistency UI shows stale data.
    if (
      !gamificationProfile ||
      gamificationProfile.emailStreaks === undefined
    ) {
      fetchGamificationProfile(true);
    }
  };

  const fetchOutreachCount = async () => {
    if (!currentOrg) return;
    setFetchPitchCountLoading(true);
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/total-outreach-count`,
      );
      setPitchCount(count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchPitchCountLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      handleFetchProfile();
      fetchOutreachCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, gamificationProfile?.id]);

  useEffect(() => {
    if (gamificationProfile && !numOfCardsToRender && !profileLoading) {
      const numOfCards = computeNicheCardToRender();
      setNumOfCardsToRender(numOfCards);
    } else if (
      currentOrg?.id &&
      (!outreachTemplates || outreachTemplates?.length === 0)
    ) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    profileLoading,
    outreachTemplates?.length,
    gamificationProfile?.id,
  ]);

  if (profileLoading) {
    return (
      <Box sx={styles.paper}>
        {!currentOrg?.isProUser && <CreateNicheTemplateCardLoading />}
        {!currentOrg?.isVaUser && <WeeklyConsistencyChallengeLoading />}
      </Box>
    );
  }

  return (
    <Box sx={styles.paper}>
      {!currentOrg?.isPaidUser &&
        (gamificationProfile?.completed ? (
          <WeeklyConsistencyChallenge
            completed={isTaskCompleted(
              gamificationProfile,
              OrganizationGamificationTask.email_streak,
              1,
            )}
          />
        ) : (
          <>
            {profile?.brandDealJourney === BrandDealJourney.work_regularly ? (
              <>
                {showSteps && (
                  <OnboardingStepper totalEmailsSent={totalEmailsSent} />
                )}
              </>
            ) : (
              <ExperienceFreeLevelOne
                pitchCount={pitchCount}
                onRefreshPitchCount={fetchOutreachCount}
                fetchPitchCountLoading={fetchPitchCountLoading}
                setShowPremiumFeatureUnlocked={setShowPremiumFeatureUnlocked}
              />
            )}

            {gamificationProfile?.unlockPremiumFeatures && (
              <>
                <CreateNicheTemplateCard
                  title={"Create Niche-Specific Template"}
                  description="Customize your pitch for a specific niche (e.g., beauty, fitness, tech)"
                  completed={isTaskCompleted(
                    gamificationProfile,
                    OrganizationGamificationTask.niche_templates,
                    1,
                  )}
                  infoBox={
                    <>
                      {" "}
                      <strong>Pro tip:</strong> Different niches respond to
                      different pitching styles. A beauty brand cares about
                      aesthetics and engagement, while a tech brand wants to see
                      expertise and audience demographics.
                    </>
                  }
                />
                <WeeklyConsistencyChallenge
                  completed={isTaskCompleted(
                    gamificationProfile,
                    OrganizationGamificationTask.email_streak,
                    1,
                  )}
                />
              </>
            )}
          </>
        ))}
      {currentOrg?.isProUser && (
        <ExperiencePro numOfCardsToRender={numOfCardsToRender} />
      )}
      {Boolean(
        currentOrg?.isVaUser && numOfCardsToRender && numOfCardsToRender > 0,
      ) && <ExperienceVa numOfCardsToRender={numOfCardsToRender} />}

      {showPremiumFeatureUnlocked && (
        <PremiumFeatureUnlocked
          isOpen={showPremiumFeatureUnlocked}
          onClose={() => setShowPremiumFeatureUnlocked(false)}
          onUpgrade={() =>
            setUpgradeDialogSource(
              UpgradeDialogSource.gamification_premium_unlocked,
            )
          }
        />
      )}
    </Box>
  );
};

export default Gamification;
